import { DEFAULT_SERVICE_SLUG } from '~/common/constants/search';

import { mapSearchResults, mapSearchResultsArgsToApiQuery } from './mappers';
import { RestApi } from '../restApi';
import { buildQueryString } from '../utils';

import type {
  GetRecommendedSittersRESTResponse,
  SearchResults,
  SearchRESTResponse,
  GetRecommendedSittersRESTRequest,
  GetSearchAddressDetailsRestResponse,
  GetRelatedSearchesLinksRESTResponse,
  GetSearchSitterPriceRESTRequest,
  GetSearchSitterPriceRESTResponse,
} from './types';
import type { AxiosError, AxiosResponse } from 'axios';
import type { SearchAddressDetails, SearchLink, SearchResultsApiArgs } from '~/common/types/search';

export class SearchApi extends RestApi {
  static async getRecommendedSitters(
    props: GetRecommendedSittersRESTRequest
  ): Promise<GetRecommendedSittersRESTResponse | null> {
    try {
      const response: AxiosResponse<GetRecommendedSittersRESTResponse> = await this.woofApi.post(
        'v1/search/recommended',
        { ...props }
      );

      return { ...response.data };
    } catch (error) {
      return this.handleError(error as AxiosError);
    }
  }

  /**
   * Returns a set of links related to the search page slug if available
   */
  static async getRelatedSearchesLinks(pageSlug: string): Promise<SearchLink[]> {
    try {
      const pageSlugComponents = pageSlug.split('/');
      const sanitisedPageSlug = pageSlugComponents
        .map((component) => encodeURIComponent(component))
        .join('/');

      const response: AxiosResponse<GetRelatedSearchesLinksRESTResponse> = await this.woofApi.get(
        `/v1/longtail`,
        {
          headers: { 'x-frontend-referrer': sanitisedPageSlug },
        }
      );

      const returnData =
        response.data.data.widget.data?.map(({ name, url }) => ({ href: url, label: name })) ?? [];
      return returnData;
    } catch (error) {
      this.handleError(error as AxiosError);

      throw error;
    }
  }

  /**
   * Get geocoded details (format, coords, postcode, state, suburb).
   * Server-side calls upstream; in browser, proxies via Next API.
   */
  static async getSearchAddressDetails(address: string): Promise<SearchAddressDetails | null> {
    let addressDetails: GetSearchAddressDetailsRestResponse;

    try {
      if (typeof window === 'undefined') {
        /* eslint-disable-next-line max-len */
        const response: AxiosResponse<GetSearchAddressDetailsRestResponse> = await this.woofApi.post(
          `${process.env.GEOCODING_BASE_URL}/geocoding`,
          {
            address,
          }
        );

        addressDetails = { ...response.data };
      } else {
        // Client-side: proxy via Next.js API to avoid CORS
        const res = await fetch('/api/location/geocoding', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ address }),
        });

        if (!res.ok) {
          return null;
        }

        const payload: GetSearchAddressDetailsRestResponse = await res.json();

        addressDetails = { ...payload };
      }

      if (!addressDetails.data) {
        return null;
      }

      const {
        formattedAddress,
        lat,
        lng,
        postcode,
        stateShortName,
        suburb,
        streetAddressLong,
      } = addressDetails.data;

      return {
        formattedAddress,
        lat,
        lng,
        postcode,
        stateName: stateShortName,
        suburb,
        streetAddressLong,
      };
    } catch (error) {
      this.handleError(error as AxiosError);

      return null;
    }
  }

  /**
   * Get the total price for a single booking,
   * given a sitter profile and the details of a given booking
   */
  static async getSearchSitterPrice(
    searchPriceArgs: GetSearchSitterPriceRESTRequest
  ): Promise<GetSearchSitterPriceRESTResponse> {
    try {
      const queryString = buildQueryString({
        ...searchPriceArgs,
      });

      const response: AxiosResponse<GetSearchSitterPriceRESTResponse> = await this.woofApi.get(
        `/v1/search/price?${queryString}`
      );

      return { ...response.data };
    } catch (error) {
      this.handleError(error as AxiosError);

      throw error;
    }
  }

  // SSR callers pass auth per request to avoid mutating the shared Axios
  // client.
  static async search(
    searchParameters: SearchResultsApiArgs,
    headers?: Record<string, string>
  ): Promise<SearchResults | null> {
    try {
      const { offset, size, ...searchQuery } = mapSearchResultsArgsToApiQuery(searchParameters);

      const data = {
        limit: size,
        page: offset / size + 1,
        offset,
        size,
        ...searchQuery,
      };

      const response: AxiosResponse<SearchRESTResponse> = await this.snifferApi.post(
        '/search',
        data,
        { headers }
      );

      const serviceSlug = searchParameters.url?.split('/')[1] ?? DEFAULT_SERVICE_SLUG;

      return mapSearchResults(response.data, serviceSlug);
    } catch (error) {
      return this.handleError(error as AxiosError);
    }
  }
}
